/**
 * Cloud Sync System for Multi-Device Data Synchronization
 * 
 * Syncs check-ins, relationships, and settings across devices
 * Uses Supabase KV store as backend with conflict resolution
 */

import { toast } from 'sonner';
import { projectId, publicAnonKey } from '/utils/supabase/info';
import { analytics } from './analytics';
import { isSupabaseOnline } from './supabase-health';

export interface SyncData {
  checkins: string | null; // JSON string of check-ins
  relationships: string | null; // JSON string of relationships
  activeRelationship: string | null;
  settings: {
    notificationSettings: string | null;
    // Legacy keys kept for backwards compatibility with older cloud payloads.
    notifications?: string | null;
    dailyReminder?: string | null;
    theme: string | null;
  };
  /** Cross-device onboarding + milestone flags (mirrors selected localStorage keys). */
  accountKeys?: Record<string, string> | null;
  lastSyncTime: number;
  deviceId: string;
}

export interface SyncStatus {
  enabled: boolean;
  lastSync: number | null;
  autoSync: boolean;
  syncInProgress: boolean;
}

const SYNC_STORAGE_KEY = 'tension_cloud_sync_enabled';
const LAST_SYNC_KEY = 'tension_last_sync_time';
const DEVICE_ID_KEY = 'tension_device_id';
const AUTO_SYNC_KEY = 'tension_auto_sync';
export const CHECKINS_UPDATED_EVENT = 'tension-checkins-updated';
export const CLOUD_BOOTSTRAP_DONE_KEY = 'tension_cloud_bootstrap_done';
export const MIN_SYNC_INTERVAL_MS = 90_000;

// Sync lock to prevent concurrent syncs
let syncInProgress = false;

function getLastSuccessfulSyncTime(): number {
  const raw = localStorage.getItem(LAST_SYNC_KEY);
  if (!raw) return 0;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

function emitLocalDataUpdatedEvent(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(CHECKINS_UPDATED_EVENT));
}

/** localStorage keys that define account progress (not device UI). */
const ACCOUNT_SYNC_KEY_PREFIXES = ['tension_milestone_', 'tension_dismissed_tease_'] as const;

const STATIC_ACCOUNT_SYNC_KEYS = [
  'onboarding_completed',
  'onboarding_skipped',
  'onboarding_completed_at',
  'onboarding_skipped_at',
  'tension_mode',
  'tension_onboarding_complete',
  'tension_user_profile',
  'tension_relationship_profile',
  'tension_milestones',
  'tension_has_seen_forecasts',
  'tension_has_seen_patterns',
  'tension_upgrade_from_milestone',
] as const;

/**
 * Collect onboarding, milestones, and engagement hints for cross-device restore.
 */
export function collectAccountKeysForSync(): Record<string, string> {
  const out: Record<string, string> = {};
  if (typeof window === 'undefined') return out;

  for (const key of STATIC_ACCOUNT_SYNC_KEYS) {
    const v = localStorage.getItem(key);
    if (v != null && v !== '') out[key] = v;
  }
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;
    if (ACCOUNT_SYNC_KEY_PREFIXES.some((p) => key.startsWith(p))) {
      const v = localStorage.getItem(key);
      if (v != null && v !== '') out[key] = v;
    }
  }
  return out;
}

/**
 * Merge server/account keys into localStorage so a new device does not replay tutorial or milestones.
 */
export function mergeAccountKeysFromRemote(remote: Record<string, string> | null | undefined): void {
  if (!remote || typeof remote !== 'object') return;

  const truthyKeys = new Set([
    'onboarding_completed',
    'onboarding_skipped',
    'tension_onboarding_complete',
  ]);

  for (const [key, remoteVal] of Object.entries(remote)) {
    if (remoteVal == null || remoteVal === '') continue;
    const localVal = localStorage.getItem(key);

    if (truthyKeys.has(key)) {
      if (remoteVal === 'true' || localVal === 'true') {
        localStorage.setItem(key, 'true');
      } else if (!localVal) {
        localStorage.setItem(key, remoteVal);
      }
      continue;
    }

    if (key === 'tension_mode') {
      if (!localVal) {
        localStorage.setItem(key, remoteVal);
      } else if (localVal !== remoteVal) {
        localStorage.setItem(key, remoteVal);
      }
      continue;
    }

    if (key.startsWith('tension_milestone_')) {
      if (remoteVal === 'true' || localVal === 'true') {
        localStorage.setItem(key, 'true');
      } else if (!localVal) {
        localStorage.setItem(key, remoteVal);
      }
      continue;
    }

    if (key.startsWith('tension_dismissed_tease_')) {
      const rt = Number.parseInt(remoteVal, 10);
      const lt = Number.parseInt(localVal || '0', 10);
      if (Number.isFinite(rt) && Number.isFinite(lt)) {
        localStorage.setItem(key, String(Math.max(rt, lt)));
      } else if (!localVal) {
        localStorage.setItem(key, remoteVal);
      }
      continue;
    }

    if (!localVal) {
      localStorage.setItem(key, remoteVal);
    }
  }
}

/**
 * Ensure critical local state exists when cloud data is restored.
 * Prevents returning users from being treated as new users after local storage loss.
 */
export function ensureRecoveredAccountState(): void {
  try {
    const relationshipsStr = localStorage.getItem('tension_relationships');
    const checkinsStr = localStorage.getItem('tension_checkins');
    const existingMode = localStorage.getItem('tension_mode');
    const existingOnboarding = localStorage.getItem('tension_onboarding_complete');

    let hasRelationships = false;
    let firstRelationshipId: string | null = null;

    if (relationshipsStr) {
      const parsed = JSON.parse(relationshipsStr);
      if (Array.isArray(parsed) && parsed.length > 0) {
        hasRelationships = true;
        firstRelationshipId = typeof parsed[0]?.id === 'string' ? parsed[0].id : null;
      }
    }

    let hasCheckins = false;
    if (checkinsStr) {
      const parsed = JSON.parse(checkinsStr);
      hasCheckins = Array.isArray(parsed) && parsed.length > 0;
    }

    if (hasRelationships && !localStorage.getItem('tension_active_relationship') && firstRelationshipId) {
      localStorage.setItem('tension_active_relationship', firstRelationshipId);
    }

    if ((hasRelationships || hasCheckins) && !existingMode) {
      localStorage.setItem('tension_mode', 'relationship');
    }

    if ((hasRelationships || hasCheckins) && !existingOnboarding) {
      localStorage.setItem('tension_onboarding_complete', 'true');
      localStorage.setItem('onboarding_completed', 'true');
    }
  } catch {
    // Keep sync resilient even if local data is malformed.
  }
}

/**
 * Generate or retrieve device ID
 */
export function getDeviceId(): string {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    deviceId = `device_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
}

/**
 * Check if cloud sync is enabled
 */
export function isSyncEnabled(): boolean {
  return localStorage.getItem(SYNC_STORAGE_KEY) === 'true';
}

/**
 * Enable cloud sync
 */
export function enableSync(): void {
  localStorage.setItem(SYNC_STORAGE_KEY, 'true');
  localStorage.setItem(AUTO_SYNC_KEY, 'true');
  analytics.track('cloud_sync_enabled');
}

/**
 * Enable cloud sync by default for signed-in users unless a preference already exists.
 */
export function ensureSyncEnabledByDefault(): void {
  if (localStorage.getItem(SYNC_STORAGE_KEY) === null) {
    localStorage.setItem(SYNC_STORAGE_KEY, 'true');
  }
  if (localStorage.getItem(AUTO_SYNC_KEY) === null) {
    localStorage.setItem(AUTO_SYNC_KEY, 'true');
  }
}

/**
 * Disable cloud sync
 */
export function disableSync(): void {
  localStorage.setItem(SYNC_STORAGE_KEY, 'false');
  localStorage.setItem(AUTO_SYNC_KEY, 'false');
  analytics.track('cloud_sync_disabled');
}

/**
 * Get sync status
 */
export function getSyncStatus(): SyncStatus {
  const lastSyncStr = localStorage.getItem(LAST_SYNC_KEY);
  return {
    enabled: isSyncEnabled(),
    lastSync: lastSyncStr ? parseInt(lastSyncStr) : null,
    autoSync: localStorage.getItem(AUTO_SYNC_KEY) === 'true',
    syncInProgress: false,
  };
}

/**
 * Collect local data for sync
 */
function collectLocalData(): SyncData {
  const notificationSettings = localStorage.getItem('tension_notification_settings');
  const accountKeys = collectAccountKeysForSync();
  return {
    checkins: localStorage.getItem('tension_checkins'),
    relationships: localStorage.getItem('tension_relationships'),
    activeRelationship: localStorage.getItem('tension_active_relationship'),
    settings: {
      notificationSettings,
      // Emit legacy fields while clients migrate to a single canonical settings key.
      notifications: localStorage.getItem('tension_notifications_enabled'),
      dailyReminder: localStorage.getItem('tension_daily_reminder'),
      theme: localStorage.getItem('tension-theme'),
    },
    accountKeys: Object.keys(accountKeys).length > 0 ? accountKeys : null,
    lastSyncTime: Date.now(),
    deviceId: getDeviceId(),
  };
}

/**
 * Apply synced data to local storage
 */
function applyRemoteData(data: SyncData, mergeStrategy: 'remote' | 'merge' = 'merge'): void {
  const remoteNotificationSettings =
    data.settings.notificationSettings ??
    (() => {
      const enabled = data.settings.notifications;
      const reminderTime = data.settings.dailyReminder;
      if (!enabled && !reminderTime) return null;
      return JSON.stringify({
        enabled: enabled === 'true',
        dailyReminder: true,
        reminderTime: reminderTime || '19:00',
      });
    })();

  if (mergeStrategy === 'remote') {
    // Replace local with remote (use for initial sync or manual override)
    if (data.checkins) localStorage.setItem('tension_checkins', data.checkins);
    if (data.relationships) localStorage.setItem('tension_relationships', data.relationships);
    if (data.activeRelationship) localStorage.setItem('tension_active_relationship', data.activeRelationship);
    if (remoteNotificationSettings) localStorage.setItem('tension_notification_settings', remoteNotificationSettings);
    if (data.settings.notifications) localStorage.setItem('tension_notifications_enabled', data.settings.notifications);
    if (data.settings.dailyReminder) localStorage.setItem('tension_daily_reminder', data.settings.dailyReminder);
    if (data.settings.theme) localStorage.setItem('tension-theme', data.settings.theme);
    if (data.accountKeys) mergeAccountKeysFromRemote(data.accountKeys);
  } else {
    // Merge strategy: combine check-ins, preserve newest settings
    mergeCheckins(data.checkins);
    mergeRelationships(data.relationships);
    
    // For settings, use remote if local doesn't exist
    if (!localStorage.getItem('tension_notification_settings') && remoteNotificationSettings) {
      localStorage.setItem('tension_notification_settings', remoteNotificationSettings);
    }
    if (!localStorage.getItem('tension_notifications_enabled') && data.settings.notifications) {
      localStorage.setItem('tension_notifications_enabled', data.settings.notifications);
    }
    if (!localStorage.getItem('tension_daily_reminder') && data.settings.dailyReminder) {
      localStorage.setItem('tension_daily_reminder', data.settings.dailyReminder);
    }
    if (data.accountKeys) mergeAccountKeysFromRemote(data.accountKeys);
  }

  ensureRecoveredAccountState();
  localStorage.setItem(LAST_SYNC_KEY, data.lastSyncTime.toString());
  emitLocalDataUpdatedEvent();
}

/**
 * Merge remote check-ins with local check-ins
 * Combines both arrays and removes duplicates based on timestamp
 */
function mergeCheckins(remoteCheckinsStr: string | null): void {
  if (!remoteCheckinsStr) return;
  
  try {
    const remoteCheckins = JSON.parse(remoteCheckinsStr);
    const localCheckinsStr = localStorage.getItem('tension_checkins');
    
    if (!localCheckinsStr) {
      localStorage.setItem('tension_checkins', remoteCheckinsStr);
      return;
    }
    
    const localCheckins = JSON.parse(localCheckinsStr);
    
    // Combine and deduplicate by timestamp
    const checkinsMap = new Map();
    
    // Add local check-ins
    localCheckins.forEach((checkin: any) => {
      const key = checkin.timestamp || checkin.date;
      if (key) checkinsMap.set(key, checkin);
    });
    
    // Add remote check-ins (will overwrite if same timestamp)
    remoteCheckins.forEach((checkin: any) => {
      const key = checkin.timestamp || checkin.date;
      if (key) checkinsMap.set(key, checkin);
    });
    
    // Convert back to array and save
    const mergedCheckins = Array.from(checkinsMap.values());
    localStorage.setItem('tension_checkins', JSON.stringify(mergedCheckins));
    
    console.log(`✅ Merged check-ins: ${localCheckins.length} local + ${remoteCheckins.length} remote = ${mergedCheckins.length} total`);
  } catch (error) {
    console.error('Error merging check-ins:', error);
  }
}

/**
 * Merge remote relationships with local relationships
 */
function mergeRelationships(remoteRelationshipsStr: string | null): void {
  if (!remoteRelationshipsStr) return;
  
  try {
    const remoteRelationships = JSON.parse(remoteRelationshipsStr);
    const localRelationshipsStr = localStorage.getItem('tension_relationships');
    
    if (!localRelationshipsStr) {
      localStorage.setItem('tension_relationships', remoteRelationshipsStr);
      return;
    }
    
    const localRelationships = JSON.parse(localRelationshipsStr);
    
    // Combine and deduplicate by id
    const relationshipsMap = new Map();
    
    localRelationships.forEach((rel: any) => {
      if (rel.id) relationshipsMap.set(rel.id, rel);
    });
    
    remoteRelationships.forEach((rel: any) => {
      if (rel.id) {
        // Keep the one with the most recent update
        const existing = relationshipsMap.get(rel.id);
        if (!existing || (rel.updatedAt && existing.updatedAt && rel.updatedAt > existing.updatedAt)) {
          relationshipsMap.set(rel.id, rel);
        }
      }
    });
    
    const mergedRelationships = Array.from(relationshipsMap.values());
    localStorage.setItem('tension_relationships', JSON.stringify(mergedRelationships));
    
    console.log(`✅ Merged relationships: ${mergedRelationships.length} total`);
  } catch (error) {
    console.error('Error merging relationships:', error);
  }
}

/**
 * Upload local data to cloud
 */
export async function pushToCloud(accessToken: string): Promise<{ success: boolean; error?: string }> {
  if (!isSyncEnabled()) {
    return { success: false, error: 'Cloud sync is not enabled' };
  }
  
  try {
    console.log('☁️ Pushing local data to cloud...');
    const localData = collectLocalData();
    
    const response = await fetch(
      `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/sync/push`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${publicAnonKey}`,
          'X-User-Token': accessToken,
        },
        body: JSON.stringify(localData),
      }
    );
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to push data: ${response.status} ${errorText}`);
    }
    
    const result = await response.json();
    
    if (result.success) {
      localStorage.setItem(LAST_SYNC_KEY, Date.now().toString());
      analytics.track('cloud_sync_push_success');
      console.log('✅ Data pushed to cloud successfully');
      return { success: true };
    } else {
      throw new Error(result.error || 'Unknown error');
    }
  } catch (error: any) {
    console.error('❌ Cloud push error:', error);
    analytics.track('cloud_sync_push_failed');
    return { success: false, error: error.message };
  }
}

/**
 * Download data from cloud and merge with local
 */
export async function pullFromCloud(
  accessToken: string,
  strategy: 'merge' | 'remote' = 'merge'
): Promise<{ success: boolean; error?: string; updated?: boolean }> {
  if (!isSyncEnabled()) {
    return { success: false, error: 'Cloud sync is not enabled' };
  }
  
  try {
    // Silent logging - don't clutter console
    if (process.env.NODE_ENV === 'development') {
      console.log('☁️ Pulling data from cloud...');
    }
    
    const response = await fetch(
      `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/sync/pull`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${publicAnonKey}`,
          'X-User-Token': accessToken,
        },
      }
    );
    
    if (!response.ok) {
      if (response.status === 404) {
        // No cloud data exists yet - do initial push (silent)
        return await pushToCloud(accessToken);
      }
      // Silent error - don't log auth errors
      if (response.status === 401) {
        return { success: false, error: 'Unauthorized' };
      }
      const errorText = await response.text();
      throw new Error(`Failed to pull data: ${response.status} ${errorText}`);
    }
    
    const result = await response.json();
    
    if (result.success && result.data) {
      const remoteData: SyncData = result.data;
      
      // Check if remote data is newer
      const lastLocalSync = localStorage.getItem(LAST_SYNC_KEY);
      const lastLocalSyncTime = lastLocalSync ? parseInt(lastLocalSync) : 0;
      
      if (remoteData.lastSyncTime > lastLocalSyncTime) {
        applyRemoteData(remoteData, strategy);
        analytics.track('cloud_sync_pull_success');
        return { success: true, updated: true };
      } else {
        return { success: true, updated: false };
      }
    } else {
      throw new Error(result.error || 'Unknown error');
    }
  } catch (error: any) {
    // Silent error handling - don't clutter console with expected failures
    analytics.track('cloud_sync_pull_failed');
    return { success: false, error: error.message };
  }
}

/**
 * Full bidirectional sync: pull then push
 */
export async function syncNow(
  accessToken: string,
  options?: { force?: boolean; minIntervalMs?: number },
): Promise<{ success: boolean; error?: string; skipped?: boolean }> {
  if (!accessToken) {
    return { success: false, error: 'Not authenticated' };
  }

  const force = !!options?.force;
  const minIntervalMs = options?.minIntervalMs ?? MIN_SYNC_INTERVAL_MS;
  if (!force) {
    const lastSync = getLastSuccessfulSyncTime();
    if (lastSync > 0 && Date.now() - lastSync < minIntervalMs) {
      return { success: true, skipped: true };
    }
  }

  // Prevent concurrent syncs
  if (syncInProgress) {
    return { success: true, skipped: true };
  }
  
  syncInProgress = true;
  
  try {
    // Silent sync - only log in dev mode
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 Starting full bidirectional sync...');
    }
    
    // Step 1: Pull remote data and merge
    const pullResult = await pullFromCloud(accessToken, 'merge');
    if (!pullResult.success) {
      return pullResult;
    }
    
    // Step 2: Push updated local data back
    const pushResult = await pushToCloud(accessToken);
    if (!pushResult.success) {
      return pushResult;
    }
    
    analytics.track('cloud_sync_full_success');
    return { success: true };
  } catch (error: any) {
    // Silent error - don't clutter console
    analytics.track('cloud_sync_full_failed');
    return { success: false, error: error.message };
  } finally {
    syncInProgress = false;
  }
}

/**
 * Auto-sync on app start (if enabled)
 */
export async function autoSyncOnStart(accessToken: string | null): Promise<void> {
  if (!accessToken || !isSyncEnabled()) return;
  
  const autoSync = localStorage.getItem(AUTO_SYNC_KEY) === 'true';
  if (!autoSync) return;
  
  // Wait 2 seconds after app start to avoid blocking
  setTimeout(async () => {
    // Silent auto-sync - no console logs
    const result = await syncNow(accessToken);
    
    // Silent success/failure - only log in development mode
    if (process.env.NODE_ENV === 'development') {
      if (result.success) {
        console.log('✅ Auto-sync completed');
      } else {
        console.log('⚠️ Auto-sync skipped (offline or not authenticated)');
      }
    }
  }, 2000);
}

/**
 * Schedule periodic auto-sync (every 5 minutes while app is open)
 */
export function startPeriodicSync(accessToken: string): () => void {
  if (!isSyncEnabled()) return () => {};
  
  const interval = setInterval(async () => {
    await syncNow(accessToken, { force: false });
  }, 5 * 60 * 1000); // 5 minutes
  
  // Return cleanup function
  return () => clearInterval(interval);
}